import * as React from 'react';

// eslint-disable-next-line
import once from 'lodash/once';
// eslint-disable-next-line
import isEmpty from 'lodash/isEmpty';

import { useApplicationContext } from '@common/react/components/Core/Application/Application';

import { UserGroup, UserGroupType } from '@commonTuna/react/objects/UserGroup';

import { User } from '@app/objects/User';

export type CheckUserGroupAccessDelegate = (userGroup: UserGroupType | Array<UserGroupType>, anyMatch?: boolean) => boolean;

export interface UserGroupContext {
	checkUserGroupAccess: CheckUserGroupAccessDelegate;
	toggleUserGroup: (userGroup: UserGroupType, active?: boolean) => Promise<UserGroup>;
}

export const createUserContext = once(() => React.createContext({} as UserGroupContext));

export const useUserGroupContext: () => UserGroupContext = () => {
	const context: UserGroupContext = React.useContext(createUserContext());

	if (isEmpty(context)) throw Error('Need UserGroup context!');

	return context;
};

interface UserGroupProviderProps {
	children: React.ReactNode | ((context: UserGroupContext) => React.ReactNode);
}

const UserGroupProvider: React.FC<UserGroupProviderProps> = ({
	children,
}) => {
	const UserGroupContext = createUserContext();
	const { getUser, request, updateUser } = useApplicationContext();
	const user = getUser<User>();

	const checkUserGroupAccess = React.useMemo(() =>
		(userGroup: UserGroupType | Array<UserGroupType>, anyMatch?: boolean): boolean => {
			return (Array.isArray(userGroup)
				? anyMatch
					? userGroup.some((ug) => user?.userGroups.find((c) => c.userGroupType === ug))
					: userGroup.every((ug) => user?.userGroups.find((c) => c.userGroupType === ug))
				: user?.userGroups.some((ug) => ug.userGroupType === userGroup) ?? false);
		}, [UserGroupContext, user]);

	const toggleUserGroup = React.useMemo(() =>
		(userGroupType: UserGroupType, active: boolean = true): Promise<UserGroup> => {
			const current = user.userGroups.find((item) => item.userGroupType === userGroupType);

			if ((active && current) || (!active && !current)) {
				return Promise.resolve<UserGroup>(current);
			}

			return request<UserGroup, UserGroup>('userGroup', current
				? {
					...current, deleted: true,
				}
				: {
					id: -1, userId: user.id, userGroupType,
				} as UserGroup)
				.then((res) => {
					updateUser({
						userGroups: current
							? user.userGroups.filter((q) => q.userGroupType !== userGroupType)
							: user.userGroups.concat(res),
					});

					return res;
				});
		}, [UserGroupContext, user]);

	const value = React.useMemo(() => {
		return { checkUserGroupAccess, toggleUserGroup };
	}, [checkUserGroupAccess, toggleUserGroup]);

	return (
		<>
			<UserGroupContext.Provider value={value}>
				{typeof children === 'function' ? children(value) : children}
			</UserGroupContext.Provider>
		</>
	);
};

export default UserGroupProvider;
